import { EmployedDialog } from "./EmployedDialog";
import { EmploymentEndedDialog } from "./EmploymentEndedDialog";
import { InterviewDialog } from "./InterviewDialog";
import { OfferDialog } from "./OfferDialog";
import { PlacementDialog } from "./PlacementDialog";
import { RollbackDialog } from "./RollbackDialog";
import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";

import { useGetPlacements } from "@/hooks/queries/useGetPlacements";
import {
  defaultPlacementQueryParams,
  isRollbackTransition,
} from "@/lib/constants";
import { CandidacyStatus, InterviewDialogMode } from "@/types";

export const StatusChangeDialogs = ({
  onStatusChanged,
  openStatusChangeDialog,
  setOpenStatusChangeDialog,
}: {
  // Fires once the candidacy actually advances, for callers that confirm the
  // move (kanban drag-and-drop). Only wired to the drag-reachable dialogs.
  onStatusChanged?: (status: CandidacyStatus) => void;
  openStatusChangeDialog?: CandidacyStatus;
  setOpenStatusChangeDialog: (status?: CandidacyStatus) => void;
}) => {
  /* Context */
  const { candidacyId } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });
  const { data: allPlacements } = useGetPlacements({
    jobId: candidacyInfo?.jobId,
    queryOptions: defaultPlacementQueryParams,
  });

  /* Helpers */
  const placements =
    allPlacements?.filter((p) => p.candidacy.id === candidacyId) ?? [];

  // A backward move out of a placement stage routes to the single RollbackDialog
  // instead of the forward dialog for that status (offer/interview schedule).
  const isRollback =
    candidacyInfo !== undefined &&
    openStatusChangeDialog !== undefined &&
    isRollbackTransition(candidacyInfo.status, openStatusChangeDialog);

  /* Handlers */
  const handleOpenChange = () => {
    setOpenStatusChangeDialog(undefined);
  };

  /* Render */
  const render = () => {
    return (
      <>
        <InterviewDialog
          mode={InterviewDialogMode.INTERVIEW}
          onOpenChange={handleOpenChange}
          open={
            openStatusChangeDialog === CandidacyStatus.INTERVIEW && !isRollback
          }
        />
        <InterviewDialog
          mode={InterviewDialogMode.SCREEN_SCHEDULED}
          onOpenChange={handleOpenChange}
          onStatusChanged={onStatusChanged}
          open={
            openStatusChangeDialog === CandidacyStatus.SCREENSCHEDULED &&
            !isRollback
          }
        />
        <InterviewDialog
          mode={InterviewDialogMode.SCREENED}
          onOpenChange={handleOpenChange}
          open={
            openStatusChangeDialog === CandidacyStatus.SCREENED && !isRollback
          }
        />
        <OfferDialog
          onOpenChange={handleOpenChange}
          onStatusChanged={onStatusChanged}
          open={openStatusChangeDialog === CandidacyStatus.OFFER && !isRollback}
        />
        <RollbackDialog
          onOpenChange={handleOpenChange}
          onStatusChanged={onStatusChanged}
          open={isRollback}
          placements={placements}
          toStatus={openStatusChangeDialog ?? CandidacyStatus.INTERVIEW}
        />
        <PlacementDialog
          onOpenChange={handleOpenChange}
          open={openStatusChangeDialog === CandidacyStatus.PLACED}
          placements={placements}
        />
        <EmployedDialog
          onOpenChange={handleOpenChange}
          open={openStatusChangeDialog === CandidacyStatus.EMPLOYED}
          placements={placements}
        />
        <EmploymentEndedDialog
          onOpenChange={handleOpenChange}
          open={openStatusChangeDialog === CandidacyStatus.EMPLOYMENT_ENDED}
          placements={placements}
        />
      </>
    );
  };

  return render();
};
